<template>
  <div class="accordion-item">
    <h2 class="accordion-header">
      <button
        class="accordion-button collapsed"
        type="button"
        data-bs-toggle="collapse"
        :data-bs-target="`#accord-${resultOrchestrator.id}-${writeInOption.reference}`"
        aria-expanded="false"
        :aria-controls="`accord-${resultOrchestrator.id}-${writeInOption.reference}`"
      >
        <strong>{{ Object.values(writeInOption["title"])[0] }}</strong>
      </button>
    </h2>
    <div class="accordion-collapse collapse" :id="`accord-${resultOrchestrator.id}-${writeInOption.reference}`"
      :aria-controls="`accord-${writeInOption.reference}`">
      <div class="accordion-body">
        <!-- Partial tallies -->
        <write-in-map-partial-tallies
          :option-reference="writeInOption.reference"
          :option-titles="optionTitles"
          :unique-write-ins="uniqueWriteIns"
          :write-in-mapping="writeInMapping"
        />

        <!-- Unique write-in values -->
        <div v-if="!writeInOption.excluded && Object.keys(uniqueWriteIns).length">
          <div class="card mb-3 mt-1 p-3">
            <strong>{{ $t("backend.e2e.voting_round_reports.show.new_option.title") }}</strong>
            {{ $t("backend.e2e.voting_round_reports.show.new_option.description") }}

            <div class="d-flex flex-column flex-xl-row gap-2 mt-3">
              <div class="w-100">
                <input
                  type="text"
                  v-model="newOptionName"
                  id="create-new-option"
                  class="form-control"
                  :class="{
                    'is-invalid': optionAlreadyExists(newOptionName) && !!submissionError,
                  }"
                  :placeholder="$t('backend.e2e.voting_round_reports.show.new_option.placeholder')"
                >
                <div
                  v-if="optionAlreadyExists(newOptionName)"
                  :class="`small ${submissionError ? 'text-danger' : 'text-muted'}`"
                >
                  <template v-if="submissionError === 'global'">
                    {{ $t("backend.e2e.voting_round_reports.show.new_option.global_already_exists") }}
                  </template>
                  <template v-else>
                    {{ $t("backend.e2e.voting_round_reports.show.new_option.already_exists") }}
                  </template>
                </div>
              </div>

              <div class="hstack gap-2 h-100">
                <button
                  class="btn btn-primary w-fit text-nowrap"
                  :disabled="disableAddOption"
                  @click="addOption()"
                >
                  <AVIcon icon="plus" />
                  {{ $t("backend.e2e.voting_round_reports.show.new_option.add_local_btn") }}
                </button>
                <button
                  class="btn btn-primary w-fit text-nowrap"
                  :disabled="!newOptionName"
                  @click="addOption('global')"
                >
                  <AVIcon icon="globe" />
                  {{ $t("backend.e2e.voting_round_reports.show.new_option.add_global_btn") }}
                </button>
              </div>
            </div>
          </div>

          <table class="table table-bordered">
            <thead>
              <tr>
                <th style="width: 30%;">{{ $t("backend.e2e.voting_round_reports.show.write_in_option") }}</th>
                <th style="width: 20%">{{ $t("backend.e2e.voting_round_reports.show.amount") }}</th>
                <th style="width: 50%">{{ $t("backend.e2e.voting_round_reports.show.mapped_to") }}</th>
              </tr>
            </thead>

            <tbody>
              <write-in-map-table-body
                v-for="uniqueWriteIn in paginatedUniqueWriteIns"
                :key="uniqueWriteIn[0]"
                :all-write-ins="Object.keys(uniqueWriteIns)"
                :unique-write-in="uniqueWriteIn"
                :write-in-mapping="writeInMapping"
                :unique-write-ins="uniqueWriteIns"
                :current-page="currentPage"
                :items-per-page="itemsPerPage"
                :option-reference="writeInOption.reference"
                :selectable-options="selectableOptions"
                :disable-select="writeInOption.excluded"
                @update-mapping="(value: string) => emits('update-mapping', uniqueWriteIn[0], value)"
              />
            </tbody>

          </table>
          <write-in-map-option-navigation
            :option-reference="writeInOption.reference"
            :total-items="Object.keys(uniqueWriteIns).length"
            :current-page="currentPage"
            :items-per-page="itemsPerPage"
            @navigate="page => emits('navigate', writeInOption.reference, page)"
          />
        </div>
        <div v-else-if="!Object.keys(uniqueWriteIns).length" class="alert alert-warning m-0">
          {{ $t('backend.e2e.voting_round_reports.show.no_write_in_votes') }}
        </div>
        <div v-else class="alert alert-warning m-0">
          {{ $t('backend.e2e.voting_round_reports.show.mapping_of_excluded_not_allowed') }}
        </div>
      </div>
    </div>

  </div>
</template>

<script setup lang="ts">
  import { ref, computed } from "vue";
  import WriteInMapTableBody from "./WriteInMapTableBody.vue";
  import WriteInMapOptionNavigation from "./WriteInMapOptionNavigation.vue";
  import WriteInMapPartialTallies from "./WriteInMapPartialTallies.vue";
  import type {
    PropType,
    ConferenceOption,
    ResultOrchestrator,
    OptionMappedWriteIns,
    MappableWriteInOption,
    WriteInMapping,
    UniqueWriteIn,
    NewWriteInOption,
  } from "@/types";
import { watch } from "vue";

  const props = defineProps({
    writeInOption: {
      type: Object as PropType<ConferenceOption>,
      required: true,
    },
    resultOrchestrator: {
      type: Object as PropType<ResultOrchestrator>,
      required: true,
    },
    writeInMappingOptions: {
      type: Object as PropType<OptionMappedWriteIns>,
      required: true,
    },
    writeInMapping: {
      type: Object as PropType<WriteInMapping>,
      required: true,
    },
    currentPage: {
      type: Number,
      required: true,
    },
    itemsPerPage: {
      type: Number,
      required: true,
    },
    optionTitles: {
      type: Object,
      required: true,
    },
    selectableOptions: {
      type: Array as PropType<MappableWriteInOption[]>,
      required: true,
    },
  });

  const emits = defineEmits(["add-global-option", "add-option", "navigate", "update-mapping"]);

  const newOptionName = ref("");

  const submissionError = ref<NewWriteInOption | null>(null);

  const uniqueWriteIns = computed(() => {
    const writeIns = props.resultOrchestrator.writeIns[props.writeInOption.reference] || [];
    const counts: Record<string, number> = {};
    for (const writeIn of writeIns) { counts[writeIn] = (counts[writeIn] || 0) + 1 };
    return counts;
  });

  const paginatedUniqueWriteIns = computed<Array<UniqueWriteIn>>(() => {
    const entries = Object.entries(uniqueWriteIns.value);
    const start = (props.currentPage - 1) * props.itemsPerPage;
    return entries.slice(start, start + props.itemsPerPage);
  });

  const optionAlreadyExists = (newOption: string) => {
    return props.writeInMappingOptions[props.writeInOption.reference].some(
      (option: MappableWriteInOption) => option.code === newOption);
  };

  const disableAddOption = computed(() => !newOptionName.value || optionAlreadyExists(newOptionName.value));

  const addOption = (type: NewWriteInOption = "local") => {
    if (optionAlreadyExists(type === "global" ? `$global_${newOptionName.value}` : newOptionName.value)) {
      submissionError.value = type;
    } else {
      submissionError.value = null;
      if (type === "global") emits("add-global-option", props.writeInOption.reference, newOptionName.value);
      else emits("add-option", props.writeInOption.reference, newOptionName.value);

      newOptionName.value = "";
    };
  };

  watch(newOptionName, (newVal, oldVal) => {
    if (optionAlreadyExists(oldVal) !== optionAlreadyExists(newVal)) {
      submissionError.value = null;
    };
  });
</script>
